import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import RestoreIcon from '@mui/icons-material/Restore';
import RefreshIcon from '@mui/icons-material/Refresh';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableViewIcon from '@mui/icons-material/TableView';
import TableContextMenu from '../../../../common/components/Table/ContextMenu';
import { ContextMenuAction } from '../../../../common/components/Table/types';
import FilenameTable from './FilenameTable';
import {
  StyledTableContainer,
  StyledTable,
  HeaderCell,
  ComplexHeaderCell,
  SubHeaderCell,
  DataCell,
  ComplexDataCell,
  GroupSeparatorRow,
} from './No3Cn56SummaryTable.styled';

export interface No3Cn56SummaryData {
  sendingPpo: string;
  receivingPpo: string;
  year: string;
  accountingType: string;
  service: string;
  mailCategory: string;
  format: string;
  product: string;
  cn56ItemsOriginal: string;
  cn56ItemsBilateral: string;
  cn56WeightOriginal: string;
  cn56WeightBilateral: string;
  no3IpkOriginal: string;
  no3IpkBilateral: string;
  no3AdjOriginal: string;
  no3AdjBilateral: string;
  no3NetOriginal: string;
  no3NetBilateral: string;
  no3Cn54BisGrossIpkOriginal: string;
  no3Cn54BisGrossIpkBilateral: string;
  direction?: 'inbound' | 'outbound';
  rowIndex?: number; // Index of the row within its group/block (used as rowType for API calls)
  alerts?: string[]; // Array of alert codes (A & B_2 for CN56 Weight Original, B_1 for NO3 Net IPK Original, C for NO3 Net IPK Bilateral, B_3 for NO3 ADJ Original)
}

// Utility function to determine which fields should be disabled for Gross and Net accounting types
export function getDisabledFields(accountingType: string, rowIndex: number) {
  // Default all fields to enabled
  const disabled = {
    cn56ItemsOriginal: false,
    cn56ItemsBilateral: false,
    cn56WeightOriginal: false,
    cn56WeightBilateral: false,
    no3IpkOriginal: false,
    no3IpkBilateral: false,
    no3AdjOriginal: false,
    no3AdjBilateral: false,
    no3NetOriginal: false,
    no3NetBilateral: false,
    no3Cn54BisGrossIpkOriginal: false,
    no3Cn54BisGrossIpkBilateral: false,
  };

  // Apply disabling logic for Net accounting type
  if (accountingType === 'N' || accountingType === 'Net') {
    // Apply the same disabling rules as Gross, but with ADJ disabled for rows 1-9
    switch (rowIndex) {
      case 0: // 1st row
      case 1: // 2nd row
      case 2: // 3rd row
        disabled.cn56ItemsOriginal = true;
        disabled.cn56ItemsBilateral = true;
        disabled.no3NetOriginal = true;
        disabled.no3NetBilateral = true;
        disabled.no3AdjOriginal = true; // ADJ disabled for Net
        disabled.no3AdjBilateral = true; // ADJ disabled for Net
        break;
      
      case 3: // 4th row
      case 4: // 5th row
      case 5: // 6th row
        disabled.cn56ItemsOriginal = true;
        disabled.cn56ItemsBilateral = true;
        disabled.no3AdjOriginal = true; // ADJ disabled for Net
        disabled.no3AdjBilateral = true; // ADJ disabled for Net
        break;
      
      case 6: // 7th row
      case 7: // 8th row
        disabled.no3IpkOriginal = true;
        disabled.no3IpkBilateral = true;
        disabled.no3NetOriginal = true;
        disabled.no3NetBilateral = true;
        disabled.no3Cn54BisGrossIpkOriginal = true;
        disabled.no3Cn54BisGrossIpkBilateral = true;
        disabled.no3AdjOriginal = true; // ADJ disabled for Net
        disabled.no3AdjBilateral = true; // ADJ disabled for Net
        break;
      
      case 8: // 9th row
        disabled.cn56ItemsOriginal = true;
        disabled.cn56ItemsBilateral = true;
        disabled.no3NetOriginal = true;
        disabled.no3NetBilateral = true;
        disabled.no3Cn54BisGrossIpkOriginal = true;
        disabled.no3Cn54BisGrossIpkBilateral = true;
        disabled.no3AdjOriginal = true; // ADJ disabled for Net
        disabled.no3AdjBilateral = true; // ADJ disabled for Net
        break;
      
      case 9: // 10th row
        disabled.cn56ItemsOriginal = true;
        disabled.cn56ItemsBilateral = true;
        disabled.no3IpkOriginal = true;
        disabled.no3IpkBilateral = true;
        disabled.no3AdjOriginal = true;
        disabled.no3AdjBilateral = true;
        disabled.no3NetOriginal = true;
        disabled.no3NetBilateral = true;
        disabled.no3Cn54BisGrossIpkOriginal = true;
        disabled.no3Cn54BisGrossIpkBilateral = true;
        break;
      
      case 10: // 11th row
      case 11: // 12th row
      case 12: // 13th row
        disabled.cn56WeightOriginal = true;
        disabled.cn56WeightBilateral = true;
        disabled.no3IpkOriginal = true;
        disabled.no3IpkBilateral = true;
        disabled.no3AdjOriginal = true;
        disabled.no3AdjBilateral = true;
        disabled.no3NetOriginal = true;
        disabled.no3NetBilateral = true;
        disabled.no3Cn54BisGrossIpkOriginal = true;
        disabled.no3Cn54BisGrossIpkBilateral = true;
        break;
      
      case 13: // 14th row
      case 14: // 15th row
      case 15: // 16th row
        disabled.cn56ItemsOriginal = true;
        disabled.cn56ItemsBilateral = true;
        disabled.no3IpkOriginal = true;
        disabled.no3IpkBilateral = true;
        disabled.no3AdjOriginal = true;
        disabled.no3AdjBilateral = true;
        disabled.no3NetOriginal = true;
        disabled.no3NetBilateral = true;
        disabled.no3Cn54BisGrossIpkOriginal = true;
        disabled.no3Cn54BisGrossIpkBilateral = true;
        break;
      
      case 16: // 17th row
        disabled.cn56ItemsOriginal = true;
        disabled.cn56ItemsBilateral = true;
        disabled.cn56WeightOriginal = true; // Added Weight original field for 17th row
        disabled.no3IpkOriginal = true;
        disabled.no3IpkBilateral = true;
        disabled.no3AdjOriginal = true;
        disabled.no3AdjBilateral = true;
        disabled.no3NetOriginal = true;
        disabled.no3NetBilateral = true;
        disabled.no3Cn54BisGrossIpkOriginal = true;
        disabled.no3Cn54BisGrossIpkBilateral = true;
        break;
      
      default:
        // For rows beyond 9 (indexes 9+), only disable ADJ for Net accounting type
        if (rowIndex >= 9) {
          // No additional ADJ disabling for rows 10+ since they're already handled above
        }
        break;
    }
    return disabled;
  }

  // Only apply Gross disabling logic for Gross accounting type
  if (accountingType !== 'G' && accountingType !== 'Gross') {
    return disabled;
  }

  // Apply disabling rules based on row index (0-based)
  switch (rowIndex) {
    case 0: // 1st row
    case 1: // 2nd row
    case 2: // 3rd row
      disabled.cn56ItemsOriginal = true;
      disabled.cn56ItemsBilateral = true;
      disabled.no3NetOriginal = true;
      disabled.no3NetBilateral = true;
      break;
    
    case 3: // 4th row
    case 4: // 5th row
    case 5: // 6th row
      disabled.cn56ItemsOriginal = true;
      disabled.cn56ItemsBilateral = true;
      break;
    
    case 6: // 7th row
    case 7: // 8th row
      disabled.no3IpkOriginal = true;
      disabled.no3IpkBilateral = true;
      disabled.no3NetOriginal = true;
      disabled.no3NetBilateral = true;
      disabled.no3Cn54BisGrossIpkOriginal = true;
      disabled.no3Cn54BisGrossIpkBilateral = true;
      break;
    
    case 8: // 9th row
      disabled.cn56ItemsOriginal = true;
      disabled.cn56ItemsBilateral = true;
      disabled.no3NetOriginal = true;
      disabled.no3NetBilateral = true;
      disabled.no3Cn54BisGrossIpkOriginal = true;
      disabled.no3Cn54BisGrossIpkBilateral = true;
      break;
    
    case 9: // 10th row
      disabled.cn56ItemsOriginal = true;
      disabled.cn56ItemsBilateral = true;
      disabled.no3IpkOriginal = true;
      disabled.no3IpkBilateral = true;
      disabled.no3AdjOriginal = true;
      disabled.no3AdjBilateral = true;
      disabled.no3NetOriginal = true;
      disabled.no3NetBilateral = true;
      disabled.no3Cn54BisGrossIpkOriginal = true;
      disabled.no3Cn54BisGrossIpkBilateral = true;
      break;
    
    case 10: // 11th row
    case 11: // 12th row
    case 12: // 13th row
      disabled.cn56WeightOriginal = true;
      disabled.cn56WeightBilateral = true;
      disabled.no3IpkOriginal = true;
      disabled.no3IpkBilateral = true;
      disabled.no3AdjOriginal = true;
      disabled.no3AdjBilateral = true;
      disabled.no3NetOriginal = true;
      disabled.no3NetBilateral = true;
      disabled.no3Cn54BisGrossIpkOriginal = true;
      disabled.no3Cn54BisGrossIpkBilateral = true;
      break;
    
    case 13: // 14th row
    case 14: // 15th row
    case 15: // 16th row
      disabled.cn56ItemsOriginal = true;
      disabled.cn56ItemsBilateral = true;
      disabled.no3IpkOriginal = true;
      disabled.no3IpkBilateral = true;
      disabled.no3AdjOriginal = true;
      disabled.no3AdjBilateral = true;
      disabled.no3NetOriginal = true;
      disabled.no3NetBilateral = true;
      disabled.no3Cn54BisGrossIpkOriginal = true;
      disabled.no3Cn54BisGrossIpkBilateral = true;
      break;
    
    case 16: // 17th row
      disabled.cn56ItemsOriginal = true;
      disabled.cn56ItemsBilateral = true;
      disabled.cn56WeightOriginal = true; // Added Weight original field for 17th row
      disabled.no3IpkOriginal = true;
      disabled.no3IpkBilateral = true;
      disabled.no3AdjOriginal = true;
      disabled.no3AdjBilateral = true;
      disabled.no3NetOriginal = true;
      disabled.no3NetBilateral = true;
      disabled.no3Cn54BisGrossIpkOriginal = true;
      disabled.no3Cn54BisGrossIpkBilateral = true;
      break;
    
    default:
      // For any other rows, keep all fields enabled
      break;
  }

  return disabled;
}

interface GroupedData {
  key: string;
  items: No3Cn56SummaryData[];
}

interface No3Cn56SummaryTableProps {
  data: any;
  isLoading: boolean;
  onEditRecord?: (row: No3Cn56SummaryData) => void;
  onResetRecord?: (row: No3Cn56SummaryData) => void;
  onResetBlock?: (groupKey: string, direction: 'inbound' | 'outbound') => void;
  onDownloadPdf?: (row: No3Cn56SummaryData) => void;
  onDownloadXls?: (row: No3Cn56SummaryData) => void;
}

// Helper component for rendering a single table
const SummaryTable = ({ 
  groupedData, 
  title, 
  direction,
  onContextMenu 
}: { 
  groupedData: GroupedData[]; 
  title: string; 
  direction: 'inbound' | 'outbound';
  onContextMenu: (
    event: React.MouseEvent,
    row: No3Cn56SummaryData,
    groupKey: string,
    direction: 'inbound' | 'outbound'
  ) => void;
}) => {
  if (!groupedData.length) return null;


  return (
    <Box sx={{ mb: 4 }}>
      <StyledTableContainer>
        <StyledTable>
          <TableHead>
            {/* Level 1: Main group headers */}
            <TableRow>
              {/* Simple columns - each spans 1 column */}
              <HeaderCell rowSpan={3}>Sending<br />DO</HeaderCell>
              <HeaderCell rowSpan={3}>Receiving<br />DO</HeaderCell>
              <HeaderCell rowSpan={3}>Year</HeaderCell>
              <HeaderCell rowSpan={3}>Accounting<br />Type</HeaderCell>
              <HeaderCell rowSpan={3}>Service</HeaderCell>
              <HeaderCell rowSpan={3}>Mail<br />Category</HeaderCell>
              <HeaderCell rowSpan={3}>Format</HeaderCell>
              <HeaderCell rowSpan={3}>Product</HeaderCell>
              
              {/* CN56 spans 4 columns */}
              <ComplexHeaderCell colSpan={4} isGroupBorder>CN56</ComplexHeaderCell>
              
              {/* NO3 spans 6 columns */}
              <ComplexHeaderCell colSpan={6} isGroupBorder>NO3</ComplexHeaderCell>
              
              {/* NO3/CN54bis spans 2 columns - Commented out for now */}
              {/* <ComplexHeaderCell colSpan={2}>NO3/CN54bis</ComplexHeaderCell> */}
            </TableRow>
            
            {/* Level 2: Sub-group headers */}
            <TableRow>
              {/* CN56 sub-headers */}
              <SubHeaderCell colSpan={2} isGroupBorder>Items</SubHeaderCell>
              <SubHeaderCell colSpan={2} isGroupBorder>Weight</SubHeaderCell>
              
              {/* NO3 sub-headers */}
              <SubHeaderCell colSpan={2} isGroupBorder >Net IPK</SubHeaderCell>
              <SubHeaderCell colSpan={2} isGroupBorder>ADJ</SubHeaderCell>
              <SubHeaderCell colSpan={2} isGroupBorder isRightGroupBorder>Net%</SubHeaderCell>
              
              {/* NO3/CN54bis sub-header - Commented out for now */}
              {/* <SubHeaderCell colSpan={2}>Gross IPK</SubHeaderCell> */}
            </TableRow>
            
            {/* Level 3: Individual column headers */}
            <TableRow>
              {/* CN56 Items */}
              <SubHeaderCell isGroupBorder>Original</SubHeaderCell>
              <SubHeaderCell>Bilateral</SubHeaderCell>
              
              {/* CN56 Weight */}
              <SubHeaderCell isGroupBorder>Original</SubHeaderCell>
              <SubHeaderCell>Bilateral</SubHeaderCell>
              
              {/* NO3 Net IPK */}
              <SubHeaderCell isGroupBorder>Original</SubHeaderCell>
              <SubHeaderCell>Bilateral</SubHeaderCell>
              
              {/* NO3 ADJ */}
              <SubHeaderCell isGroupBorder>Original</SubHeaderCell>
              <SubHeaderCell>Bilateral</SubHeaderCell>
              
              {/* NO3 Net% */}
              <SubHeaderCell isGroupBorder>Original</SubHeaderCell>
              <SubHeaderCell isRightGroupBorder >Bilateral</SubHeaderCell>
              
              {/* NO3/CN54bis Gross IPK - Commented out for now */}
              {/* <SubHeaderCell>Original</SubHeaderCell> */}
              {/* <SubHeaderCell>Bilateral</SubHeaderCell> */}
            </TableRow>
          </TableHead>
          
          <TableBody>
            {groupedData.map((group, groupIndex) => (
              <React.Fragment key={group.key}>
                {group.items.map((row: No3Cn56SummaryData, index: number) => {
                  // Get disabled fields for this row
                  const disabledFields = getDisabledFields(row.accountingType, row.rowIndex || 0);
                  
                  // Check if this is the first row of the group
                  const isFirstRowOfGroup = index === 0;

                  return (
                    <TableRow 
                      key={`${group.key}-${index}`}
                      onContextMenu={(event) => onContextMenu(event, row, group.key, direction)}
                      sx={{ 
                        cursor: 'context-menu',
                        '&:hover': {
                          backgroundColor: direction === 'inbound' 
                            ? 'rgba(76, 175, 80, 0.1)' 
                            : 'rgba(244, 67, 54, 0.1)',
                        }
                      }}
                    >
                      {/* Simple columns */}
                      <DataCell direction={direction} isFirstRowOfGroup={isFirstRowOfGroup}>{row.sendingPpo || ''}</DataCell>
                      <DataCell direction={direction} isFirstRowOfGroup={isFirstRowOfGroup}>{row.receivingPpo || ''}</DataCell>
                      <DataCell direction={direction} isFirstRowOfGroup={isFirstRowOfGroup}>{row.year || ''}</DataCell>
                      <DataCell direction={direction} isFirstRowOfGroup={isFirstRowOfGroup}>{row.accountingType || ''}</DataCell>
                      <DataCell direction={direction} isFirstRowOfGroup={isFirstRowOfGroup}>{row.service || ''}</DataCell>
                      <DataCell direction={direction} isFirstRowOfGroup={isFirstRowOfGroup}>{row.mailCategory || ''}</DataCell>
                      <DataCell direction={direction} isFirstRowOfGroup={isFirstRowOfGroup}>{row.format || ''}</DataCell>
                      <DataCell direction={direction} isFirstRowOfGroup={isFirstRowOfGroup}>{row.product || ''}</DataCell>
                      
                      {/* CN56 Items */}
                      <ComplexDataCell 
                        className="number-cell" 
                        direction={direction}
                        isBilateral={!!row.cn56ItemsBilateral && row.cn56ItemsBilateral.trim() !== ''}
                        isDisabled={disabledFields.cn56ItemsOriginal}
                        isGroupBorder
                        isFirstRowOfGroup={isFirstRowOfGroup}
                      >
                        {row.cn56ItemsOriginal || ''}
                      </ComplexDataCell>
                      <ComplexDataCell 
                        className="number-cell" 
                        direction={direction}
                        isDisabled={disabledFields.cn56ItemsBilateral}
                        isFirstRowOfGroup={isFirstRowOfGroup}
                      >
                        {row.cn56ItemsBilateral || ''}
                      </ComplexDataCell>
                      
                      {/* CN56 Weight */}
                      <ComplexDataCell 
                        className="number-cell" 
                        direction={direction}
                        isBilateral={!!row.cn56WeightBilateral && row.cn56WeightBilateral.trim() !== ''}
                        isDisabled={disabledFields.cn56WeightOriginal}
                        isGroupBorder
                        isFirstRowOfGroup={isFirstRowOfGroup}
                        hasAlertA={row.alerts?.includes('A') || row.alerts?.includes('B_2') || false}
                      >
                        {row.cn56WeightOriginal || ''}
                      </ComplexDataCell>
                      <ComplexDataCell 
                        className="number-cell" 
                        direction={direction}
                        isDisabled={disabledFields.cn56WeightBilateral}
                        isFirstRowOfGroup={isFirstRowOfGroup}
                      >
                        {row.cn56WeightBilateral || ''}
                      </ComplexDataCell>
                      
                      {/* NO3 Net IPK */}
                      <ComplexDataCell 
                        className="number-cell" 
                        direction={direction}
                        isBilateral={!!row.no3IpkBilateral && row.no3IpkBilateral.trim() !== ''}
                        isDisabled={disabledFields.no3IpkOriginal}
                        isGroupBorder
                        isFirstRowOfGroup={isFirstRowOfGroup}
                        hasAlertB1={row.alerts?.includes('B_1') || false}
                      >
                        {row.no3IpkOriginal || ''}
                      </ComplexDataCell>
                      <ComplexDataCell 
                        className="number-cell" 
                        direction={direction}
                        isDisabled={disabledFields.no3IpkBilateral}
                        isFirstRowOfGroup={isFirstRowOfGroup}
                        hasAlertA={row.alerts?.includes('C') || false}
                      >
                        {row.no3IpkBilateral || ''}
                      </ComplexDataCell>
                      
                      {/* NO3 ADJ */}
                      <ComplexDataCell 
                        className="number-cell" 
                        direction={direction}
                        isBilateral={!!row.no3AdjBilateral && row.no3AdjBilateral.trim() !== ''}
                        isDisabled={disabledFields.no3AdjOriginal}
                        isGroupBorder
                        isFirstRowOfGroup={isFirstRowOfGroup}
                        hasAlertB1={row.alerts?.includes('B_1') || false}
                      >
                        {row.no3AdjOriginal || ''}
                      </ComplexDataCell>
                      <ComplexDataCell 
                        className="number-cell" 
                        direction={direction}
                        isDisabled={disabledFields.no3AdjBilateral}
                        isFirstRowOfGroup={isFirstRowOfGroup}
                      >
                        {row.no3AdjBilateral || ''}
                      </ComplexDataCell>
                      
                      {/* NO3 Net% */}
                      <ComplexDataCell 
                        className="number-cell" 
                        direction={direction}
                        isBilateral={!!row.no3NetBilateral && row.no3NetBilateral.trim() !== ''}
                        isDisabled={disabledFields.no3NetOriginal}
                        isGroupBorder
                        isFirstRowOfGroup={isFirstRowOfGroup}
                        hasAlertB1={row.alerts?.includes('B_1') || false}
                      >
                        {row.no3NetOriginal || ''}
                      </ComplexDataCell>
                      <ComplexDataCell 
                        className="number-cell" 
                        direction={direction}
                        isDisabled={disabledFields.no3NetBilateral}
                        isRightGroupBorder
                        isFirstRowOfGroup={isFirstRowOfGroup}
                      >
                        {row.no3NetBilateral || ''}
                      </ComplexDataCell>
                      
                      {/* NO3/CN54bis Gross IPK - Commented out for now */}
                      {/*
                      <ComplexDataCell 
                        className="number-cell" 
                        direction={direction}
                        isBilateral={!!row.no3Cn54BisGrossIpkBilateral && row.no3Cn54BisGrossIpkBilateral.trim() !== ''}
                        isDisabled={disabledFields.no3Cn54BisGrossIpkOriginal}
                      >
                        {row.no3Cn54BisGrossIpkOriginal || ''}
                      </ComplexDataCell>
                      <ComplexDataCell 
                        className="number-cell" 
                        direction={direction}
                        isDisabled={disabledFields.no3Cn54BisGrossIpkBilateral}
                      >
                        {row.no3Cn54BisGrossIpkBilateral || ''}
                      </ComplexDataCell>
                      */}
                    </TableRow>
                  );
                })}
                {/* Add bold separator line at the end of each group (except the last group) */}
                {groupIndex < groupedData.length - 1 && (
                  <GroupSeparatorRow>
                    <TableCell colSpan={16} />
                  </GroupSeparatorRow>
                )}
              </React.Fragment>
            ))}
          </TableBody>
        </StyledTable>
      </StyledTableContainer>
    </Box>
  );
};

export function No3Cn56SummaryTable({ 
  data, 
  isLoading,
  onEditRecord,
  onResetRecord,
  onResetBlock,
  onDownloadPdf,
  onDownloadXls
}: No3Cn56SummaryTableProps) {
  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    row: No3Cn56SummaryData | null;
    groupKey: string;
    direction: 'inbound' | 'outbound';
  } | null>(null);

  // Handle context menu open
  const handleContextMenu = useCallback((
    event: React.MouseEvent,
    row: No3Cn56SummaryData,
    groupKey: string,
    direction: 'inbound' | 'outbound'
  ) => {
    event.preventDefault();
    setContextMenu({
      mouseX: event.clientX + 2,
      mouseY: event.clientY - 6,
      row,
      groupKey,
      direction,
    });
  }, []);

  // Handle context menu close
  const handleContextMenuClose = useCallback(() => {
    setContextMenu(null);
  }, []);

  // Handle context menu actions
  const handleContextMenuAction = useCallback((action: ContextMenuAction) => {
    if (!contextMenu?.row) return;

    const { row, groupKey, direction } = contextMenu;

    switch (action) {
      case 'editRecord':
        onEditRecord?.(row);
        break;
      case 'resetRecord':
        onResetRecord?.(row);
        break;
      case 'resetBlock':
        onResetBlock?.(groupKey, direction);
        break;
      case 'downloadPdf':
        onDownloadPdf?.(row);
        break;
      case 'downloadXls':
        onDownloadXls?.(row);
        break;
      default:
        console.log('Unhandled action:', action);
    }
    
    setContextMenu(null);
  }, [contextMenu, onEditRecord, onResetRecord, onResetBlock, onDownloadPdf, onDownloadXls]);
  // Transform the data structure for the table, preserving grouping
  const { inboundData, outboundData } = useMemo(() => {
    if (!data) return { inboundData: [], outboundData: [] };
    
    const inboundData: GroupedData[] = [];
    const outboundData: GroupedData[] = [];
    
    // Process inbound data - handle new nested structure
    if (data.inbound) {
      // Check if data has the new nested structure with no3Cn56Summary
      if (data.inbound.no3Cn56Summary) {
        Object.entries(data.inbound.no3Cn56Summary).forEach(([key, group]: [string, any]) => {
          if (Array.isArray(group)) {
            const inboundGroup = group.map((item: any, index: number) => ({
              ...item,
              direction: 'inbound' as const,
              rowIndex: index
            }));
            inboundData.push({
              key,
              items: inboundGroup
            });
          }
        });
      } else {
        // Fallback to old structure for backward compatibility
        Object.entries(data.inbound).forEach(([key, group]: [string, any]) => {
          if (Array.isArray(group)) {
            const inboundGroup = group.map((item: any, index: number) => ({
              ...item,
              direction: 'inbound' as const,
              rowIndex: index
            }));
            inboundData.push({
              key,
              items: inboundGroup
            });
          }
        });
      }
    }
    
    // Process outbound data - handle new nested structure
    if (data.outbound) {
      // Check if data has the new nested structure with no3Cn56Summary
      if (data.outbound.no3Cn56Summary) {
        Object.entries(data.outbound.no3Cn56Summary).forEach(([key, group]: [string, any]) => {
          if (Array.isArray(group)) {
            const outboundGroup = group.map((item: any, index: number) => ({
              ...item,
              direction: 'outbound' as const,
              rowIndex: index
            }));
            outboundData.push({
              key,
              items: outboundGroup
            });
          }
        });
      } else {
        // Fallback to old structure for backward compatibility
        Object.entries(data.outbound).forEach(([key, group]: [string, any]) => {
          if (Array.isArray(group)) {
            const outboundGroup = group.map((item: any, index: number) => ({
              ...item,
              direction: 'outbound' as const,
              rowIndex: index
            }));
            outboundData.push({
              key,
              items: outboundGroup
            });
          }
        });
      }
    }
    
    return { inboundData, outboundData };
  }, [data]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!inboundData.length && !outboundData.length) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: '#FFF8E1',
          borderRadius: '4px',
          border: '1px dashed #FFE082',
        }}
      >
        <Typography
          sx={{
            fontStyle: 'italic',
            color: '#F57F17',
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          No NO3 & CN56 Summary data available
        </Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ mt: 2, p: 2 }}>
      {/* Inbound File Status */}
      {data?.inbound && (
        <FilenameTable
          eno1FileInfos={data.inbound.eno1FileInfos || []}
          e55FileInfos={data.inbound.e55FileInfos || []}
          e09FileInfos={data.inbound.e09FileInfos || []}
          direction="inbound"
        />
      )}
      
      {/* Inbound Table */}
      <SummaryTable 
        groupedData={inboundData} 
        title="Inbound Data" 
        direction="inbound"
        onContextMenu={handleContextMenu}
      />
      
      {/* Outbound File Status */}
      {data?.outbound && (
        <FilenameTable
          eno1FileInfos={data.outbound.eno1FileInfos || []}
          e55FileInfos={data.outbound.e55FileInfos || []}
          e09FileInfos={data.outbound.e09FileInfos || []}
          direction="outbound"
        />
      )}
      
      {/* Outbound Table */}
      <SummaryTable 
        groupedData={outboundData} 
        title="Outbound Data" 
        direction="outbound"
        onContextMenu={handleContextMenu}
      />

      {/* Context Menu */}
      <TableContextMenu
        open={contextMenu !== null}
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : null
        }
        onClose={handleContextMenuClose}
        onAction={handleContextMenuAction}
        hideDefaultActions={true}
        customActions={[
          {
            action: 'editRecord',
            label: 'Edit Record',
            icon: <EditIcon sx={{ fontSize: '16px' }} />,
          },
          {
            action: 'resetRecord',
            label: 'Reset Record',
            icon: <RestoreIcon sx={{ fontSize: '16px' }} />,
          },
          {
            action: 'resetBlock',
            label: 'Reset Block',
            icon: <RefreshIcon sx={{ fontSize: '16px' }} />,
          },
          {
            action: 'downloadPdf',
            label: 'Download PDF',
            icon: <PictureAsPdfIcon sx={{ fontSize: '16px' }} />,
          },
          {
            action: 'downloadXls',
            label: 'Download XLS',
            icon: <TableViewIcon sx={{ fontSize: '16px' }} />,
          },
        ]}
        currentRow={contextMenu?.row}
      />
    </Paper>
  );
} 